import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { affiliateUrl, isAffiliate } from "@/lib/affiliate";
import { ArrowLeft, ArrowRight, Camera, Eye, Loader2, Ticket } from "lucide-react";

function money(n) {
  const v = Number(n);
  return Number.isInteger(v) ? `£${v}` : `£${v.toFixed(2)}`;
}

function priceNumber(t) {
  const m = (t.starting_from || "").match(/[\d,]+\.?\d*/);
  if (!m) return null;
  const v = parseFloat(m[0].replace(/,/g, ""));
  return Number.isNaN(v) ? null : v;
}

function Heading({ children }) {
  return (
    <h2 className="flex items-center gap-3 font-serif text-2xl font-semibold text-amber-50">
      <span className="h-6 w-1 rounded-full bg-[#C89B3C]" /> {children}
    </h2>
  );
}

export default function ShowPrices() {
  const { id } = useParams();
  const [show, setShow] = useState(null);

  useEffect(() => {
    setShow(null);
    api.get(`/shows/${id}`).then((r) => setShow(r.data)).catch(() => setShow(false));
  }, [id]);

  if (show === null) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C89B3C]" />
      </div>
    );
  }
  if (show === false) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-center font-serif text-2xl">
        <Seo title="Show not found" noindex />
        Show not found.
      </div>
    );
  }

  const showPath = `/shows/${show.slug || show.id}`;
  const isClosed = show.status === "Closed";
  const guide = show.price_guide;
  const areas = guide?.areas || [];
  const sellers = [...(show.ticket_links || [])].sort(
    (a, b) => (priceNumber(a) ?? Infinity) - (priceNumber(b) ?? Infinity)
  );
  const prices = sellers.map(priceNumber).filter((p) => p !== null);
  const cheapest = prices.length > 1 ? Math.min(...prices) : null;
  const thin = areas.length === 0 && !guide?.overall && sellers.length < 2;
  const lowestSeller = prices.length ? Math.min(...prices) : null;
  const overallLow = guide?.overall
    ? lowestSeller !== null
      ? Math.min(guide.overall.from, lowestSeller)
      : guide.overall.from
    : null;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <Seo
        title={`${show.name} ticket prices by seat area`}
        description={`Compare ${show.name} ticket prices across sellers and see roughly what each part of ${show.venue} costs.`}
        path={`${showPath}/prices`}
        image={show.image_url}
        noindex={thin}
      />

      <Link to={showPath} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to {show.name}
      </Link>
      <h1 className="mt-4 font-serif text-4xl font-semibold text-amber-50">{show.name} ticket prices</h1>
      <p className="mt-2 text-amber-100/70">
        Compare sellers and see roughly what each part of {show.venue} costs.
      </p>

      <section className="mt-10">
        <Heading>Sellers compared</Heading>
        {isClosed ? (
          <p className="mt-4 text-muted-foreground">This show has closed, so tickets are no longer on sale.</p>
        ) : sellers.length === 0 ? (
          <p className="mt-4 text-muted-foreground">We have no seller links for this show yet.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {sellers.map((t, i) => {
              const isCheapest = cheapest !== null && priceNumber(t) === cheapest;
              return (
                <a
                  key={i}
                  href={affiliateUrl(t.url)}
                  target="_blank"
                  rel={isAffiliate(t.url) ? "sponsored noopener noreferrer" : "noopener noreferrer"}
                  className={`flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#161118]/70 px-4 py-3 transition-colors hover:border-[#C89B3C]/60 ${
                    isCheapest ? "ring-1 ring-[#C89B3C]/50" : ""
                  }`}
                >
                  <span className="flex items-center gap-2 font-medium text-amber-50">
                    <Ticket className="h-4 w-4 text-[#C89B3C]" /> {t.name}
                    {isCheapest && (
                      <span className="rounded-full bg-[#C89B3C] px-2 py-0.5 text-[11px] font-bold text-black">cheapest</span>
                    )}
                  </span>
                  <span className="text-[#C89B3C]">{t.starting_from ? `from ${t.starting_from}` : "Check price"}</span>
                </a>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          "From" prices are indicative and change daily. Check each seller for the price on your date.
          {sellers.some((t) => isAffiliate(t.url)) && " Some seller links are affiliate links: we may earn a commission if you book, at no extra cost to you."}
        </p>
      </section>

      {(areas.length > 0 || guide?.overall) && (
        <section className="mt-12">
          <Heading>Price guide by seat area</Heading>
          {guide.overall && (
            <p className="mt-3 text-amber-50/85">
                            Tickets for this show typically range from {money(overallLow)} to {money(guide.overall.to)}.
            </p>
          )}
          {areas.length > 0 && (
            <ul className="mt-4 divide-y divide-white/10 rounded-2xl border border-[#C89B3C]/25 bg-[#161118]/70 px-5">
              {areas.map((a, i) => (
                <li key={i} className="flex items-baseline justify-between gap-4 py-3">
                  <span className="font-medium text-[#C89B3C]">{a.area}</span>
                  <span className="text-right text-amber-50/90">
                  {a.from == null
                      ? `up to ${money(a.to)}`
                      : a.to
                      ? `${money(a.from)} to ${money(a.to)}`
                      : `from ${money(a.from)}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {guide.note && <p className="mt-3 text-sm leading-6 text-amber-50/80">{guide.note}</p>}
          <p className="mt-3 text-xs leading-5 text-muted-foreground">
            Approximate prices, which change by date and demand. {guide.source}
            {guide.checked ? `, checked ${guide.checked}` : ""}. Check each seller for today's price.
          </p>
        </section>
      )}

      {(show.theatre_slug || show.seat_view_url || show.seatplan_url) && (
        <section className="mt-12">
          <Heading>Choose your seat</Heading>
          <div className="mt-4 flex flex-wrap gap-3">
            {show.theatre_slug && (
              <Link
                to={`/theatres/${show.theatre_slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-[#C89B3C] bg-[#C89B3C]/15 px-4 py-2 text-sm font-semibold text-[#C89B3C] transition-colors hover:bg-[#C89B3C]/30"
              >
                <Eye className="h-4 w-4" /> Where to sit at {show.venue}
              </Link>
            )}
            {show.seat_view_url && (
              <a
                href={show.seat_view_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-4 py-2 text-sm font-semibold text-black transition-transform hover:scale-105"
              >
                <Camera className="h-4 w-4" /> See photos from the seats <ArrowRight className="h-4 w-4" />
              </a>
            )}
            {show.seatplan_url && (
              <a
                href={show.seatplan_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-amber-50 transition-colors hover:bg-white/10"
              >
                Seating plan on SeatPlan
              </a>
            )}
          </div>
        </section>
      )}
    </div>
  );
}